import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Icon } from "src/app/components/icon/Icon";
import { FORM_TYPES } from "src/app/modules/auth/constants";
import logoBlue from "src/assets/logos/logo-blue.svg";
import { ROLE, ROUTES } from "src/config/constants";
import { ModalContext } from "src/contexts/ModalContext";
import { IAuth } from "src/types/global";

import headerCSS from "./Header.module.css";

const Header = ({
  userData,
  showAuthModal,
  handleLogout,
}: {
  userData: IAuth | undefined | null;
  showAuthModal: (formType: string) => void;
  handleLogout: () => void;
}) => {
  const [menuActive, setMenuActive] = useState(false);
  const modalData = useContext(ModalContext);
  const menuRef = useRef<Array<any>>([]);
  const setRef = (key: number, ref: any) => {
    menuRef.current[key] = ref;
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current[0]?.contains(event.target as Node) && !menuRef.current[1]?.contains(event.target as Node) && menuActive) {
        setTimeout(function () {
          setMenuActive(false);
        }, 250);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [menuRef, menuActive]);

  function logout() {
    modalData?.triggerSuccess({
      title: "Uspješna odjava",
      message: "Uspješno ste se odjavili iz sustava",
    });
    handleLogout();
  }

  return (
    <div className={classNames("u-bg-primary-dark", headerCSS.headContainer)}>
      <nav className="navbar u-bg-primary-dark">
        <div className={classNames("u-bg-white", "u-flex", "u-flex-content-center", "u-flex-items-center", "u-pos-relative", headerCSS.logoContainer)}>
          <Link to={{ pathname: "https://knauf.hr" }} target="_blank" className={classNames(headerCSS.knaufLink)}>
            <img alt="Knauf Logo" className={classNames(headerCSS.logo)} src={logoBlue} />
          </Link>
        </div>
        <div className={classNames(headerCSS.titleContainer)}>
          <button className="u-t-nodecoration u-p0 u-cursor-pointer" onClick={() => (window.location.href = ROUTES.home)}>
            <h3 className="u-t-uppercase u-t-white">Kalkulator</h3>
          </button>
        </div>
        <SubHeader setRef={setRef} userData={userData} menuActive={menuActive} showAuthModal={showAuthModal} handleLogout={logout} toggleMenu={setMenuActive} />
        <NavBurger setRef={setRef} toggleMenu={setMenuActive} menuActive={menuActive} />
        <SubHeaderMobile setRef={setRef} userData={userData} menuActive={menuActive} showAuthModal={showAuthModal} handleLogout={logout} />
      </nav>
    </div>
  );
};

export default Header;

const SubHeader = ({
  userData,
  menuActive,
  showAuthModal,
  handleLogout,
  toggleMenu,
  setRef,
}: {
  userData: IAuth | undefined | null;
  menuActive: boolean;
  showAuthModal: (formType: string) => void;
  handleLogout: () => void;
  toggleMenu: (active: boolean) => void;
  setRef: (key: number, refElem: any) => void;
}) => {
  return (
    <div className={headerCSS.menuContainer}>
      {userData?.isVerified ? (
        <div className={classNames("u-flex", "u-flex-items-base", "u-flex-content-center")}>
          {userData?.role === ROLE.Admin && (
            <div className="u-flex u-mr20px u-align-self-center">
              <Link to={ROUTES.adminStatistics} className="u-t-nodecoration u-mr35px u-t-uppercase u-cursor-pointer">
                <h4 className="u-t-white u-t-w700 u-m0">Statistika</h4>
              </Link>
            </div>
          )}
          <div className="u-flex u-mr20px u-align-self-center">
            <Link to={ROUTES.showUserProjects(userData.id)} className="u-t-nodecoration u-mr35px u-t-uppercase u-cursor-pointer">
              <h4 className="u-t-white u-t-w700 u-m0">Moji Projekti</h4>
            </Link>
          </div>
          {(userData?.role === ROLE.Admin || userData?.role === ROLE.RegionalAdmin) && (
            <div className="u-flex u-mr20px u-align-self-center">
              <Link to={ROUTES.adminProjects} className="u-t-nodecoration u-mr35px u-t-uppercase u-cursor-pointer">
                <h4 className="u-t-white u-t-w700 u-m0">Upiti</h4>
              </Link>
            </div>
          )}
          {userData?.role === ROLE.Admin && (
            <>
              <div className="u-flex u-align-self-center">
                <Link to={ROUTES.adminUsersActive} className="u-t-nodecoration u-mr35px u-t-uppercase u-cursor-pointer">
                  <h4 className="u-t-white u-t-w700 u-m0">Korisnici</h4>
                </Link>
              </div>
            </>
          )}
          <div ref={(refElem) => setRef(0, refElem)} className="u-pos-relative u-inline-block u-align-self-center">
            <button
              className={classNames("u-cursor-pointer", menuActive ? headerCSS.activeMenu : "", headerCSS.dropdownBtn)}
              onClick={() => toggleMenu(!menuActive)}
            >
              <h4 className={classNames("u-t-white", "u-t-w700", "u-mt0", "u-mb0", "u-ml0", "u-mr10px", menuActive ? "u-t-primary-dark" : "")}>
                {`${userData?.firstName} ${userData?.lastName}`}
              </h4>
              <Icon name="arrow-down" className={classNames("u-cursor-pointer", menuActive ? headerCSS.activeMenuIcon : "u-t-white", "u-t-f24p")} />
            </button>
            <div
              className={classNames(menuActive ? "" : "u-none", "u-pos-absolute", "u-bg-white", "u-t-align-center", "u-z-index5", headerCSS.dropdownContent)}
            >
              <hr className="u-w85 u-bg-primary-dark" />
              <Link to={ROUTES.showUserProfile(userData.id)} className="u-t-nodecoration u-block u-t-uppercase u-cursor-pointer">
                <h4 className="u-t-f20 u-t-primary-dark u-t-w700 u-mt0 u-mb0 u-ml0">Moj Profil</h4>
              </Link>
              <button
                onClick={() => handleLogout()}
                className="u-t-uppercase u-flex u-flex-items-center u-flex-content-center u-w100 u-pb10px u-cursor-pointer"
              >
                <Icon name="log-out" className="u-mr5per u-t-primary-dark" />
                <h4 className="u-t-f20 u-t-primary-dark u-t-w700 u-mt0 u-mb0 u-ml0 u-inline-block">odjava</h4>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <li
          className={classNames("u-cursor-pointer", "u-t-uppercase", "u-t-white", "u-list-style-none", "u-ml10per", "u-flex", "u-flex-content-center")}
          onClick={() => showAuthModal(FORM_TYPES.LOGIN)}
        >
          <h4 className="u-t-uppercase u-t-white">Prijava / Registracija</h4>
        </li>
      )}
    </div>
  );
};

const NavBurger = ({
  menuActive,
  toggleMenu,
  setRef,
}: {
  menuActive: boolean;
  toggleMenu: (active: boolean) => void;
  setRef: (key: number, refElem: any) => void;
}) => {
  return (
    <div
      ref={(refElem) => setRef(1, refElem)}
      id="mobile-menu"
      className={classNames("u-cursor-pointer", headerCSS.mobileNav, menuActive ? headerCSS.menuActive : "")}
      onClick={() => toggleMenu(!menuActive)}
    >
      <span className={classNames(headerCSS.bar)}></span>
      <span className={classNames(headerCSS.bar)}></span>
      <span className={classNames(headerCSS.bar)}></span>
    </div>
  );
};

const SubHeaderMobile = ({
  userData,
  menuActive,
  showAuthModal,
  handleLogout,
  setRef,
}: {
  userData: IAuth | undefined | null;
  menuActive: boolean;
  showAuthModal: (formType: string) => void;
  handleLogout: () => void;
  setRef: (key: number, refElem: any) => void;
}) => {
  return (
    <ul className={classNames(menuActive ? "mobile-nav" : "u-none", "nav", "u-z-index5", headerCSS.mobileList)}>
      {userData?.isVerified ? (
        <div>
          {userData?.role === ROLE.Admin && (
            <li>
              <Link to={ROUTES.adminStatistics} className="u-t-nodecoration u-t-uppercase u-cursor-pointer">
                <h4 className="u-t-white u-t-w700 u-m0">Statistika</h4>
              </Link>
            </li>
          )}
          <li className="u-cursor-pointer u-t-uppercase">
            <Link to={ROUTES.showUserProjects(userData.id)} className="u-t-nodecoration u-cursor-pointer">
              <h4 className="u-t-white u-t-w700 u-m0">Moji Projekti</h4>
            </Link>
          </li>
          {(userData?.role === ROLE.Admin || userData?.role === ROLE.RegionalAdmin) && (
            <li>
              <Link to={ROUTES.adminProjects} className="u-t-nodecoration u-t-uppercase u-cursor-pointer">
                <h4 className="u-t-white u-t-w700 u-m0">Upiti</h4>
              </Link>
            </li>
          )}
          {userData?.role === ROLE.Admin && (
            <li>
              <Link to={ROUTES.adminUsersActive} className="u-t-nodecoration u-t-uppercase u-cursor-pointer">
                <h4 className="u-t-white u-t-w700 u-m0">Korisnici</h4>
              </Link>
            </li>
          )}
          <hr className="u-w85" />
          <div className={classNames("u-flex", "u-flex-content-between", headerCSS.userLog)}>
            <div className="u-flex">
              <Link to={ROUTES.showUserProfile(userData.id)} className="u-t-nodecoration u-flex u-flex-items-center u-t-uppercase u-cursor-pointer u-opacity-1">
                <Icon name="user" className="u-mr10px u-t-white" />
                <h4 className="u-t-white u-m0 u-t-w400">{`${userData?.firstName} ${userData?.lastName}`}</h4>
              </Link>
            </div>
            <div>
              <button
                onClick={() => handleLogout()}
                className="u-t-uppercase u-t-nodecoration u-flex u-flex-items-center u-flex-content-center u-w100 u-pt5px u-pb5px u-cursor-pointer"
              >
                <h4 className="u-t-uppercase u-m0 u-t-white u-t-w400">odjava</h4>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <li
          className="u-cursor-pointer u-t-uppercase u-t-white u-flex u-flex-content-center u-flex-items-base u-mb50px"
          onClick={() => showAuthModal(FORM_TYPES.LOGIN)}
        >
          <Icon name="user" className="u-mr10px" />
          <h4 className="u-t-uppercase u-t-white u-m0">Prijava / Registracija</h4>
        </li>
      )}
    </ul>
  );
};
